<template>
  <div class="h-full">
    <div
      ref="sidebarRef"
      class="flex h-full flex-col border-none pb-6 transition-all duration-200"
      :class="{ 'bg-neutral-100 dark:bg-neutral-800': isMobile }"
      :style="{ width: widthStyle }"
    >
      <!-- Logo Container -->
      <div class="box-border" :class="minimized ? 'px-5' : 'px-6'">
        <div class="box-border h-[70px] border-0 border-b border-solid border-neutral-200 dark:border-neutral-50/10">
          <component :is="!route.meta?.classRoom ? 'RouterLink' : 'span'" to="/" title="Home" class="aspect-square" tabindex="-1">
            <template v-if="!minimized">
              <div class="h-full w-full flex justify-between items-center">
                <div class="grow w-full flex items-center justify-center">
                  <div v-if="logos.loginLight" class="grow-0 text-center">
                    <img :src="systemStore.isDark ? logos.loginDark : logos.loginLight" alt="" class="block mx-auto object-contain h-full w-auto max-w-[200px] max-h-[42px]" />
                  </div>
                  <DefaultMenuLogo v-else />
                </div>

                <UiButton type="ghost" class="md:hidden h-10 w-10" @click.stop.prevent="() => toggleSize()">
                  <UiIcon icon="chevrons-left" :size="24" class="transition-all" :class="{ '-scale-x-100': minimized }" />
                </UiButton>
              </div>
            </template>
            <span v-else class="flex items-center justify-center w-full h-full">
              <img v-if="logos.menu" :src="logos.menu" alt="" class="w-6 h-6 object-cover" />
              <DefaultMenuLogo v-else compact />
            </span>
          </component>
        </div>
      </div>

      <!-- items -->
      <div class="grow overflow-hidden">
        <OverlayScrollbarsComponent defer :options="{ scrollbars: { autoHide: 'leave' } }" class="h-full text-neutral-700 pb-20">
          <div v-if="!route.meta?.classRoom" class="flex flex-col gap-2 mt-2">
            <SidebarSearch v-if="sideBarMenu.length > 0" class="px-4 pt-4">
              <template #default="{ isActive }">
                <template v-if="!minimized">
                  <div class="flex items-center gap-3 px-4 py-[10px] dark:hover:text-[--c-primary-200]">
                    <div v-if="isActive" class="absolute left-0 h-6 w-0.5 rounded-sm bg-[--c-primary-900] dark:bg-[--c-primary-200]" />
                    <UiIcon icon="search" :size="32" class="shrink-0" />
                    <span class="grow">{{ $t('input.search') }}</span>
                  </div>
                </template>
                <div v-else class="flex h-12 w-12 items-center justify-center">
                  <div v-if="isActive" class="absolute left-0 h-6 w-0.5 rounded-sm bg-[--c-primary-900] dark:bg-[--c-primary-200]" />
                  <UiIcon icon="search" :size="32" />
                </div>
              </template>
            </SidebarSearch>
            <SidebarItem
              v-for="(item, index) in sideBarMenu"
              :key="index"
              :icon="item.icon"
              :name="typeof item.title === 'string' ? $t(item.title) : $t(item.title[0], item.title[1])"
              :items="item.items"
              :to="item.to || ''"
              class="px-4"
              :active="hasCrumb([item.name])"
              :minimized
            >
              <template v-if="!minimized && item.items" #items>
                <template v-for="(subItem, subIndex) in item.items" :key="subIndex">
                  <RouterLink v-if="!subItem?.target"
                    :to="subItem.to"
                    :class="['sb-menu-item', { 'has-crumb': hasCrumb([item.name, subItem.name]) }]"
                    @click="() => { if (isMobile) toggleSize() }"
                  >
                    <Transition appear mode="out-in" name="scale-y-0">
                      <div v-if="hasCrumb([item.name, subItem.name])" class="absolute left-0 h-4 w-0.5 rounded-sm bg-[--c-primary-900] dark:bg-[--c-primary-200]" />
                    </Transition>
                    <span class="grow">{{ typeof subItem.title === 'string' ? $t(subItem.title) : $t(subItem.title[0], subItem.title[1]) }}</span>
                  </RouterLink>
                  <a v-else
                    :href="subItem.to"
                    :target="subItem?.target"
                    class="sb-menu-item"
                    @click="() => { if (isMobile) toggleSize() }"
                  >
                    <span class="grow">{{ typeof subItem.title === 'string' ? $t(subItem.title) : $t(subItem.title[0], subItem.title[1]) }}</span>
                  </a>
                </template>
              </template>
              <template v-else-if="minimized && item.items" #compactItems="{ close }">
                <div class="my-6 mx-4 space-y-3">
                  <div class="text-base text-neutral-900 dark:text-neutral-300 mb-6">
                    {{ typeof item.title === 'string' ? $t(item.title) : $t(item.title[0], item.title[1]) }}
                  </div>

                  <div v-for="(subItem, subIndex) in item.items" :key="subIndex" class="text-neutral-800 dark:text-neutral-200">
                    <RouterLink
                      v-if="!subItem?.target"
                      :to="subItem.to"
                      :class="['sb-menu-item text-inherit block !rounded-md', { 'has-crumb': hasCrumb([item.name, subItem.name]) }]"
                      @click="() => close()"
                    >
                      <Transition appear mode="out-in" name="scale-y-0">
                        <div v-if="hasCrumb([item.name, subItem.name])" class="absolute left-0 h-4 w-0.5 rounded-sm bg-[--c-primary-900] dark:bg-[--c-primary-200]"></div>
                      </Transition>
                      <span class="grow">{{ typeof subItem.title === 'string' ? $t(subItem.title) : $t(subItem.title[0], subItem.title[1]) }}</span>
                    </RouterLink>
                    <a v-else
                      :href="subItem.to"
                      :target="subItem?.target"
                      class="sb-menu-item !text-neutral-500 block !rounded-md"
                      @click="() => close()"
                    >
                      <span class="grow">{{ typeof subItem.title === 'string' ? $t(subItem.title) : $t(subItem.title[0], subItem.title[1]) }}</span>
                    </a>
                  </div>
                </div>
              </template>
            </SidebarItem>
          </div>
          <div v-else id="main-menu-otherside"></div>
          <div class="mx-4 my-2 w-[calc(100%-32px)] border-t border-[#e0e0e0] dark:border-neutral-50/10"></div>
          <SidebarItem
            :icon="!minimized ? 'layout-sidebar-left-collapse' : 'layout-sidebar-left-expand'"
            :name="$t(minimized ? 'menu.expandMenu' : 'menu.collapseMenu')"
            class="px-4 truncate"
            :minimized
            @click="toggleSize"
          ></SidebarItem>
        </OverlayScrollbarsComponent>
      </div>

      <div class="mt-6">
        <div class="px-3 flex flex-wrap gap-2">
          <RouterLink v-if="isMobile && route.meta?.classRoom" to="/">
            <UiButton
              type="danger"
              outline
              class="py-2 px-2"
              v-tooltip="{ placement: 'bottom' }"
              :title="$t('button.leaveClassroom')"
            >
              <UiIcon icon="door-exit" :size="20" class="-scale-x-100" />
              <span class="text-base">{{ $t('button.exit') }}</span>
            </UiButton>
          </RouterLink>

          <VersionCheck :compact="minimized" />

          <!-- User -->
          <UserMenu :compact="minimized" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useWindowSize } from '@vueuse/core'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue'
import { useSystemStore } from '@/stores'

import UiButton from '@/components/ui/button.vue'
import UiIcon from '@/components/ui/icon.vue'

import UserMenu from './userMenu.vue'
import SidebarItem from './sidebarItem.vue'
import SidebarSearch from './sidebarSearch.vue'
import DefaultMenuLogo from './defaultMenuLogo.vue'

import VersionCheck from './versionCheck.vue'

import { useSidebarMenu } from '@/composables/useSidebarMenu'

const props = defineProps<{
  maxWidth?: number | string
  minWidth?: number
  minimized?: boolean
}>()

const emit = defineEmits(['onMinimize'])

const route = useRoute()
const { width } = useWindowSize()
const { sideBarMenu } = useSidebarMenu()

const hasCrumb = (names: string[]) => (route.meta.breadcrumbs as string[])?.length && names.every((name) => (route.meta.breadcrumbs as string[])?.indexOf(name) !== -1)

const systemStore = useSystemStore()

const openItems = ref([])
provide('openItems', openItems)

const logos = computed(() => systemStore.clientSettings.settings.logos)
const isMobile = computed(() => width.value < 768)

const widthStyle = computed(() => {
  const width = !props.minimized ? props.maxWidth || '260px' : props.minWidth || '80px';
  return  typeof width === 'number' ? `${width}px` : width
})

const toggleSize = () => {
  emit('onMinimize')
}
</script>

<style scoped lang="scss">
.sb-menu-item {
  @apply relative box-border cursor-pointer flex items-center rounded-r-md py-[10px] px-4 transition-all first:mt-3 no-underline;

  &.has-crumb {
    @apply bg-[--c-primary-600] font-semibold text-[--c-primary-at-800] hover:text-[--c-primary-at-900] dark:text-[--c-primary-at-600];
    > span {
      @apply dark:text-[--c-primary-at-600];
    }
  }

  &:not(.has-crumb) {
    @apply text-inherit hover:bg-neutral-200/75 hover:text-black dark:text-neutral-200 dark:hover:bg-neutral-700 dark:hover:text-neutral-300;
  }
}
</style>